import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Card, Checkbox, Column, EmptyState, Select, Table } from "stories/Admin";
import useFetch from "hooks/useFetch";
import { RoutePaths } from "enums/RoutePaths";
import SearchField, { matchesSearch } from "./SearchField";
import styles from "./ExternalPage.module.scss";
import {
  BonnetidCatalogSource,
  CatalogCandidate,
  CatalogCoverage,
  CatalogMosqueRef,
  ExternalJamatRegistration,
  FIELD_LABELS,
  FetchOneResult,
  MissingTimes,
  RegisteredNotInCatalog,
  isValidSourceId,
} from "./bonnetidTypes";

interface BonnetidToolsPanelProps {
  registrations: ExternalJamatRegistration[];
  /** Åpner «Koble til annen kilde» for registreringen, ev. med et forslag til ny kilde. */
  onChangeSource: (registration: ExternalJamatRegistration, suggestedSource?: string) => void;
  /** Noe er synket/endret: last oversikten på nytt. */
  onChanged: () => void;
}

interface PickItem {
  key: string;
  sourceOrgNr: string;
  title: string;
  subtitle: string;
  registered: boolean;
}

interface LocalLocation { id: number; name: string; }
interface PrayerTable { id: number; name: string; }

const MAX_PICK_ROWS = 30;

const fieldList = (fields: Array<{ field: string }>) =>
  fields.map((f) => FIELD_LABELS[f.field] ?? f.field).join(", ");

/**
 * Bonnetid-verktøy: hent én moské og sjekk manglende moskeer.
 * Moskédata fra Bonnetid fyller bare inn tomme felter – det superadmin har redigert, overskrives ikke.
 */
const BonnetidToolsPanel: React.FC<BonnetidToolsPanelProps> = ({ registrations, onChangeSource, onChanged }) => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const navigate = useNavigate();
  const [source, setSource] = useState<BonnetidCatalogSource>("NEW");
  const [includePrayerTimes, setIncludePrayerTimes] = useState(true);

  // --- Hent én moské
  const [catalog, setCatalog] = useState<CatalogCandidate[] | null>(null);
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [pickSearch, setPickSearch] = useState("");
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [fetchResult, setFetchResult] = useState<FetchOneResult | null>(null);
  const [fetchError, setFetchError] = useState("");

  // --- Sjekk manglende moskeer
  const [coverage, setCoverage] = useState<CatalogCoverage | null>(null);
  const [coverageBusy, setCoverageBusy] = useState(false);
  const [coverageError, setCoverageError] = useState("");
  const [importing, setImporting] = useState<CatalogMosqueRef | null>(null);
  const [locals, setLocals] = useState<LocalLocation[]>([]);
  const [tables, setTables] = useState<PrayerTable[]>([]);
  const [importLocation, setImportLocation] = useState("");
  const [importTable, setImportTable] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importMessage, setImportMessage] = useState("");

  const loadCatalog = useCallback(async (catalogSource: BonnetidCatalogSource) => {
    setCatalogBusy(true);
    const rows = (await executeRef.current("/api/v1/admin/calendar/bonnetid/mosques", "GET", undefined, {
      source: catalogSource,
    })) as CatalogCandidate[] | null;
    setCatalogBusy(false);
    if (rows === null) {
      setFetchError(lastError()?.message || "Kunne ikke hente Bonnetid-katalogen.");
      return;
    }
    setCatalog(rows);
  }, []);

  const pickItems = useMemo<PickItem[]>(() => {
    const registered = registrations.map((r) => ({
      key: `reg-${r.id}`,
      sourceOrgNr: r.sourceOrgNr || r.orgNr,
      title: r.mosqueName || r.orgNr,
      subtitle: r.sourceChanged ? `${r.orgNr} · kilde ${r.sourceOrgNr}` : r.orgNr,
      registered: true,
    }));
    const registeredSources = new Set(registered.map((r) => r.sourceOrgNr));
    const fromCatalog = (catalog ?? [])
      .filter((c) => !registeredSources.has(c.orgNr))
      .map((c) => ({
        key: `cat-${c.orgNr}`,
        sourceOrgNr: c.orgNr,
        title: c.name,
        subtitle: [c.orgNr, c.city, c.existing ? "finnes i iBønn" : "ikke i iBønn"].filter(Boolean).join(" · "),
        registered: false,
      }));
    return [...registered, ...fromCatalog]
      .filter((item) => matchesSearch(pickSearch, [item.title, item.subtitle, item.sourceOrgNr]))
      .slice(0, MAX_PICK_ROWS);
  }, [registrations, catalog, pickSearch]);

  const fetchOne = async (sourceOrgNr: string) => {
    setFetchingId(sourceOrgNr);
    setFetchError("");
    setFetchResult(null);
    const result = (await execute("/api/v1/admin/external-jamat/fetch-one", "POST", {
      source,
      orgNr: sourceOrgNr,
      includePrayerTimes,
    })) as FetchOneResult | null;
    setFetchingId(null);
    if (!result) {
      setFetchError(lastError()?.message || "Kunne ikke hente moskeen.");
      return;
    }
    setFetchResult(result);
    if (result.mosques.length > 0) onChanged();
  };

  const checkCoverage = async () => {
    setCoverageBusy(true);
    setCoverageError("");
    const result = (await execute("/api/v1/admin/external-jamat/coverage", "GET", undefined, {
      source,
      days: 7,
    })) as CatalogCoverage | null;
    setCoverageBusy(false);
    if (!result) {
      setCoverageError(lastError()?.message || "Kunne ikke sammenligne med Bonnetid.");
      return;
    }
    setCoverage(result);
  };

  const startImport = async (ref: CatalogMosqueRef) => {
    setImporting(ref);
    setImportMessage("");
    if (locals.length === 0 || tables.length === 0) {
      const [t, l] = await Promise.all([
        executeRef.current("/api/v1/prayer-times-tables", "GET"),
        executeRef.current("/api/v1/locations", "GET"),
      ]);
      const tableRows = (t as PrayerTable[] | null) ?? [];
      setTables(tableRows);
      setLocals(((l as LocalLocation[] | null) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)));
      const standard = tableRows.find((item) => /bønnetid|bonnetid|^standard$/i.test(item.name));
      if (standard) setImportTable(String(standard.id));
    }
    const city = locals.find((l) => l.name.toLowerCase() === ref.city.toLowerCase());
    setImportLocation(city ? String(city.id) : "");
  };

  const doImport = async () => {
    if (!importing || !importLocation || !importTable) return;
    setImportBusy(true);
    const result = await execute("/api/v1/admin/calendar/bonnetid/mosques/import", "POST", {
      source,
      orgNr: importing.orgNr,
      locationId: Number(importLocation),
      prayerTableId: Number(importTable),
    });
    setImportBusy(false);
    if (result === null) {
      setImportMessage(lastError()?.message || "Kunne ikke importere moskeen.");
      return;
    }
    setImportMessage(`${importing.name} er importert. Trykk «Hent nå» for å hente moskédata og tider.`);
    setCoverage((c) => (c ? { ...c, missingInIbonn: c.missingInIbonn.filter((m) => m.orgNr !== importing.orgNr) } : c));
    setCatalog(null);
  };

  const openMosque = (orgNr: string) => navigate(RoutePaths.MosqueAdmin, { state: { search: orgNr } });

  const registrationById = (id: number) => registrations.find((r) => r.id === id);

  const missingColumns: Array<Column<CatalogMosqueRef>> = [
    { key: "name", header: "Moské i Bonnetid", render: (m) => <><b>{m.name}</b><span className={styles.sourceNote}>{m.orgNr}</span></> },
    { key: "address", header: "Adresse", render: (m) => [m.address, m.postalCode, m.city].filter(Boolean).join(" ") },
    {
      key: "actions",
      header: "Handling",
      render: (m) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="secondary" onClick={() => startImport(m)}>Importer</Button>
          <Button size="sm" variant="secondary" loading={fetchingId === m.orgNr} onClick={() => fetchOne(m.orgNr)}>Hent nå</Button>
        </div>
      ),
    },
  ];

  const goneColumns: Array<Column<RegisteredNotInCatalog>> = [
    {
      key: "name",
      header: "Registrert i iBønn",
      render: (r) => (
        <>
          <b>{r.mosqueName || r.orgNr}</b>
          <span className={styles.sourceNote}>
            {r.orgNr}
            {r.sourceOrgNr !== r.orgNr && ` · kilde ${r.sourceOrgNr}`}
            {!r.isActive && " · pauset"}
          </span>
        </>
      ),
    },
    {
      key: "suggestions",
      header: "Ligner i Bonnetid",
      render: (r) =>
        r.suggestions.length === 0 ? (
          <span className={styles.muted}>Ingen forslag</span>
        ) : (
          r.suggestions.map((s) => (
            <span key={s.orgNr} className={styles.sourceNote}>
              {s.name} · {s.orgNr}
            </span>
          ))
        ),
    },
    {
      key: "actions",
      header: "Handling",
      render: (r) => {
        const reg = registrationById(r.registrationId);
        return (
          <div className={styles.rowActions}>
            {reg && r.suggestions[0] && (
              <Button size="sm" onClick={() => onChangeSource(reg, r.suggestions[0].orgNr)}>
                Koble til {r.suggestions[0].orgNr}
              </Button>
            )}
            {reg && (
              <Button size="sm" variant="secondary" onClick={() => onChangeSource(reg)}>
                Koble til annen kilde
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => openMosque(r.orgNr)}>Åpne</Button>
          </div>
        );
      },
    },
  ];

  const timesColumns: Array<Column<MissingTimes>> = [
    {
      key: "name",
      header: "Moské",
      render: (m) => (
        <>
          <b>{m.mosqueName || m.orgNr}</b>
          <span className={styles.sourceNote}>
            {m.orgNr}
            {m.sourceOrgNr !== m.orgNr && ` · kilde ${m.sourceOrgNr}`}
            {!m.isActive && " · pauset"}
          </span>
        </>
      ),
    },
    {
      key: "days",
      header: "Uten jamat",
      numeric: true,
      render: (m) => <Badge tone={m.daysWithoutJamat === m.daysChecked ? "rose" : "gold"}>{m.daysWithoutJamat} av {m.daysChecked} dager</Badge>,
    },
    {
      key: "actions",
      header: "Handling",
      render: (m) => {
        const reg = registrationById(m.registrationId);
        return (
          <div className={styles.rowActions}>
            <Button size="sm" variant="secondary" loading={fetchingId === m.sourceOrgNr} onClick={() => fetchOne(m.sourceOrgNr)}>Hent nå</Button>
            {reg && <Button size="sm" variant="secondary" onClick={() => onChangeSource(reg)}>Koble til annen kilde</Button>}
            <Button size="sm" variant="ghost" onClick={() => openMosque(m.orgNr)}>Åpne</Button>
          </div>
        );
      },
    },
  ];

  const typedId = pickSearch.trim();

  return (
    <Card title="Bonnetid-verktøy">
      <div className={styles.dialogRow}>
        <Select
          label="Katalog"
          value={source}
          onChange={(e) => {
            setSource(e.target.value as BonnetidCatalogSource);
            setCatalog(null);
            setCoverage(null);
          }}
        >
          <option value="NEW">Nytt Bønnetid-API</option>
          <option value="LEGACY">Gammelt Bønnetid-API</option>
        </Select>
        <Checkbox
          label="Også bønnetider for byen (denne og neste måned)"
          checked={includePrayerTimes}
          onChange={(e) => setIncludePrayerTimes(e.target.checked)}
        />
      </div>
      <p className={styles.muted}>
        Moskédata fra Bonnetid (navn, adresse, by, koordinater, logo, nettside) fyller bare inn tomme felter. Felter du
        har redigert i iBønn, Asr-metode og «Kun kart» overskrives aldri.
      </p>

      <section className={styles.toolSection}>
        <h3>Hent én moské</h3>
        <SearchField value={pickSearch} onChange={setPickSearch} placeholder="Søk etter registrert moské, navn eller org.nr. …" />
        <div className={styles.rowActions}>
          {catalog === null && (
            <Button size="sm" variant="secondary" loading={catalogBusy} onClick={() => loadCatalog(source)}>
              Søk også i Bonnetid-katalogen
            </Button>
          )}
          {isValidSourceId(typedId) && (
            <Button size="sm" variant="secondary" loading={fetchingId === typedId} onClick={() => fetchOne(typedId)}>
              Hent «{typedId}»
            </Button>
          )}
        </div>
        {pickSearch.trim() && (
          <ul className={styles.pickList} aria-label="Moskeer å hente">
            {pickItems.map((item) => (
              <li key={item.key}>
                <button type="button" disabled={fetchingId !== null} onClick={() => fetchOne(item.sourceOrgNr)}>
                  <b>{item.title}</b>
                  <span className={styles.muted}>{item.subtitle}</span>
                  {item.registered && <Badge tone="blue">Registrert</Badge>}
                  {fetchingId === item.sourceOrgNr && <span className={styles.muted}>Henter …</span>}
                </button>
              </li>
            ))}
            {pickItems.length === 0 && <li className={styles.muted}>Ingen treff.</li>}
          </ul>
        )}
        {fetchError && <Alert tone="error">{fetchError}</Alert>}
        {fetchResult && (
          <Alert tone={fetchResult.errors.length ? "warn" : fetchResult.mosques.length ? "ok" : "info"}>
            <b>{fetchResult.sourceName ? `${fetchResult.sourceName} · ` : ""}{fetchResult.sourceOrgNr}</b>: {fetchResult.message}
            {fetchResult.mosques.map((m) => (
              <span key={m.orgNr} className={styles.sourceNote}>
                {m.name || m.orgNr} ({m.orgNr}){m.linkedBySourceChange && " – koblet via annen kilde"}
                {m.registrationCreated && " – ny Bonnetid-registrering"}:{" "}
                {m.syncLog ? `+${m.syncLog.jamatDaysCreated} jamat-dager, +${m.syncLog.jummahRowsCreated} jummah` : "ikke synket"}
                {m.fieldsFilled.length > 0 ? `; fylt inn: ${fieldList(m.fieldsFilled)}` : "; ingen moskéfelt endret"}
              </span>
            ))}
            {fetchResult.prayerTimes.map((p, i) => (
              <span key={i} className={styles.sourceNote}>Bønnetider: {p}</span>
            ))}
            {fetchResult.errors.map((e, i) => (
              <span key={`e${i}`} className={styles.sourceNote}>Feil: {e}</span>
            ))}
          </Alert>
        )}
      </section>

      <section className={styles.toolSection}>
        <h3>Sjekk manglende moskeer</h3>
        <Button variant="secondary" loading={coverageBusy} onClick={checkCoverage}>Sjekk manglende moskeer</Button>
        {coverageError && <Alert tone="error">{coverageError}</Alert>}
        {coverage && (
          <>
            <p className={styles.muted}>
              {coverage.catalogMosques} moskeer i katalogen · {coverage.registrations} registrert i iBønn
            </p>
            <h4>I Bonnetid, men ikke i iBønn ({coverage.missingInIbonn.length})</h4>
            <Table columns={missingColumns} rows={coverage.missingInIbonn} rowKey={(m) => m.orgNr} compact
              empty={<EmptyState icon="check" title="Ingen mangler">Alle moskeene i katalogen finnes i iBønn.</EmptyState>} />
            {importing && (
              <Card title={`Importer ${importing.name}`}>
                <p>{importing.orgNr} · {[importing.address, importing.postalCode, importing.city].filter(Boolean).join(" ")}</p>
                <div className={styles.dialogRow}>
                  <Select label="Lokal by" value={importLocation} onChange={(e) => setImportLocation(e.target.value)}>
                    <option value="">Velg by</option>
                    {locals.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Select>
                  <Select label="Bønnetidstabell" value={importTable} onChange={(e) => setImportTable(e.target.value)}>
                    <option value="">Velg tabell</option>
                    {tables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Select>
                  <Button loading={importBusy} disabled={!importLocation || !importTable} onClick={doImport}>Importer denne moskeen</Button>
                  <Button variant="secondary" onClick={() => setImporting(null)}>Lukk</Button>
                </div>
                {importMessage && <Alert tone="info">{importMessage}</Alert>}
              </Card>
            )}
            <h4>Registrert i iBønn, men finnes ikke lenger i Bonnetid ({coverage.registeredNotInCatalog.length})</h4>
            <Table columns={goneColumns} rows={coverage.registeredNotInCatalog} rowKey={(r) => r.registrationId} compact
              empty={<EmptyState icon="check" title="Alle finnes">Alle registrerte kilder finnes i katalogen.</EmptyState>} />
            <h4>Uten jamat-tider de neste 7 dagene ({coverage.missingTimes.length})</h4>
            <Table columns={timesColumns} rows={coverage.missingTimes} rowKey={(m) => m.registrationId} compact
              empty={<EmptyState icon="check" title="Alle har tider">Alle registrerte moskeer har jamat de neste dagene.</EmptyState>} />
          </>
        )}
      </section>

    </Card>
  );
};

export default BonnetidToolsPanel;
